/**
 * Conditionally register service worker.
 * Skips registration when running inside Capacitor native shell.
 */
import { Capacitor } from '@capacitor/core'

export async function registerServiceWorker(): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    return
  }

  // In dev, never run a service worker: unregister any left over from a prior
  // production/preview build and drop its caches, otherwise it keeps serving a
  // stale bundle and live code changes never reach the browser.
  if (import.meta.env.DEV) {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations()
      await Promise.all(regs.map((r) => r.unregister()))
    }
    if (typeof caches !== 'undefined') {
      const keys = await caches.keys()
      await Promise.all(keys.map((k) => caches.delete(k)))
    }
    return
  }

  const { registerSW } = await import('virtual:pwa-register')

  // registerType is 'autoUpdate' (vite.config): a new service worker skip-waits,
  // claims clients and reloads automatically once it's detected. But the browser
  // only looks for a new SW on navigation, so a PWA left open can sit on a stale
  // bundle after a deploy. Poll for a new version hourly and whenever the app
  // regains focus / comes back online so updates reach users promptly (#767).
  registerSW({
    immediate: true,
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return

      const check = () => { registration.update().catch(() => {}) }

      setInterval(check, 60 * 60 * 1000)
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') check()
      })
      window.addEventListener('online', check)
    },
  })
}
